import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import {
  User, Mail, Shield, Loader2, Save, BadgeCheck, Share2, Send,
  Star, ShoppingBag, Calendar, TrendingUp, Bell, Camera,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ProfilePage = () => {
  const { user, getAuthHeaders, updateProfile, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [saving, setSaving] = useState(false);
  const [uploadingPic, setUploadingPic] = useState(false);
  const fileInputRef = useRef(null);

  // Referrals
  const [referralEmail, setReferralEmail] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [sendingReferral, setSendingReferral] = useState(false);

  // Provider analytics
  const [providerStats, setProviderStats] = useState(null);

  // Notification preferences
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const authOpts = useCallback(() => ({ headers: getAuthHeaders(), withCredentials: true }), [getAuthHeaders]);
  const isProvider = user?.role === 'lawyer' || user?.role === 'broker';

  useEffect(() => {
    const load = async () => {
      try {
        const [refsRes, statsRes, prefsRes] = await Promise.all([
          axios.get(`${API}/referrals/my`, authOpts()).catch(() => ({ data: [] })),
          axios.get(`${API}/referrals/stats`, authOpts()).catch(() => ({ data: null })),
          axios.get(`${API}/notifications/preferences`, authOpts()).catch(() => ({ data: null })),
        ]);
        setReferrals(refsRes.data);
        setReferralStats(statsRes.data);
        setNotifPrefs(prefsRes.data);
      } catch {}

      if (isProvider || user?.role === 'admin') {
        try {
          const res = await axios.get(`${API}/analytics/provider`, authOpts());
          setProviderStats(res.data);
        } catch {}
      }
    };
    load();
  }, [authOpts, isProvider, user?.role]);

  const handleSave = async () => {
    setSaving(true);
    try { await updateProfile({ name, bio }); toast.success('Profile updated!'); }
    catch { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Only image files allowed'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5MB)'); return; }
    setUploadingPic(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/users/me/picture`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      toast.success('Profile picture updated!');
      if (refreshUser) refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upload'); }
    finally { setUploadingPic(false); }
  };

  const handleSendReferral = async () => {
    if (!referralEmail.trim()) return;
    setSendingReferral(true);
    try {
      await axios.post(`${API}/referrals/`, { referred_email: referralEmail }, authOpts());
      toast.success('Referral sent!');
      setReferralEmail('');
      const [refsRes, statsRes] = await Promise.all([
        axios.get(`${API}/referrals/my`, authOpts()),
        axios.get(`${API}/referrals/stats`, authOpts()),
      ]);
      setReferrals(refsRes.data);
      setReferralStats(statsRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send referral'); }
    finally { setSendingReferral(false); }
  };

  const handleSavePrefs = async () => {
    if (!notifPrefs) return;
    setSavingPrefs(true);
    try { await axios.put(`${API}/notifications/preferences`, notifPrefs, authOpts()); toast.success('Preferences saved!'); }
    catch { toast.error('Failed to save preferences'); }
    finally { setSavingPrefs(false); }
  };

  const getRoleBadgeVariant = (role) => {
    const map = { admin: 'destructive', lawyer: 'default', broker: 'secondary' };
    return map[role] || 'outline';
  };

  return (
    <div className="min-h-screen bg-background page-with-bottom-nav" data-testid="profile-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 pt-24 pb-12 space-y-6">
        {/* Profile Card */}
        <Card className="card-default animate-slide-up">
          <CardHeader className="text-center pb-8">
            <div className="mx-auto mb-4 relative group">
              <Avatar className="h-24 w-24 border-4 border-primary/20">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="text-2xl bg-primary/10 text-primary">{user?.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <input type="file" ref={fileInputRef} accept="image/*" onChange={handlePictureUpload} className="hidden" data-testid="picture-file-input" />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPic}
                className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:bg-primary/90 transition-colors"
                data-testid="upload-picture-btn"
              >
                {uploadingPic ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              </button>
            </div>
            <CardTitle className="font-serif text-2xl flex items-center justify-center gap-2">
              {user?.name}
              {user?.is_verified && <BadgeCheck className="h-5 w-5 text-blue-500" />}
            </CardTitle>
            <CardDescription className="flex items-center justify-center gap-2">
              <Badge variant={getRoleBadgeVariant(user?.role)}>{user?.role}</Badge>
              {user?.is_approved ? (
                <Badge variant="outline" className="text-green-500 border-green-500">Approved</Badge>
              ) : (
                <Badge variant="outline" className="text-yellow-500 border-yellow-500">Pending</Badge>
              )}
              {user?.is_verified && <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Verified Provider</Badge>}
            </CardDescription>
          </CardHeader>
          <Separator />
          <CardContent className="pt-6 space-y-6">
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2"><User className="h-4 w-4" /> Account Information</h3>
              <div className="grid gap-4">
                <div className="flex items-center gap-3 text-sm"><Mail className="h-4 w-4 text-muted-foreground" /><span className="text-muted-foreground">Email:</span><span>{user?.email}</span></div>
                <div className="flex items-center gap-3 text-sm"><Shield className="h-4 w-4 text-muted-foreground" /><span className="text-muted-foreground">Role:</span><span className="capitalize">{user?.role}</span></div>
              </div>
            </div>
            <Separator />
            <div className="space-y-4">
              <h3 className="font-semibold">Edit Profile</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Display Name</Label>
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)} className="input-default" data-testid="profile-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea id="bio" placeholder="Tell us about yourself..." value={bio} onChange={(e) => setBio(e.target.value)} className="input-default min-h-[100px]" data-testid="profile-bio-input" />
                </div>
                <Button onClick={handleSave} disabled={saving} className="w-full btn-primary" data-testid="save-profile-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Changes
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Provider Analytics */}
        {providerStats && (isProvider || user?.role === 'admin') && (
          <Card className="card-default" data-testid="provider-analytics">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg"><TrendingUp className="h-5 w-5 text-primary" /> Provider Analytics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { icon: ShoppingBag, label: 'Services', value: providerStats.total_services },
                  { icon: Star, label: 'Avg Rating', value: providerStats.average_rating || 'N/A' },
                  { icon: TrendingUp, label: 'Purchases', value: providerStats.total_purchases },
                  { icon: Calendar, label: 'Meetings', value: providerStats.total_meetings },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="text-center p-3 rounded-lg bg-muted/30">
                    <Icon className="h-5 w-5 mx-auto text-primary mb-1" />
                    <p className="text-xl font-bold">{value}</p>
                    <p className="text-xs text-muted-foreground">{label}</p>
                  </div>
                ))}
              </div>
              {providerStats.monthly_revenue > 0 && (
                <div className="mt-4 p-3 rounded-lg bg-green-500/5 border border-green-500/20 text-center">
                  <p className="text-sm text-muted-foreground">Monthly Revenue</p>
                  <p className="text-2xl font-bold text-green-500">${providerStats.monthly_revenue}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Referral Program */}
        <Card className="card-default" data-testid="referral-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><Share2 className="h-5 w-5 text-primary" /> Refer a Friend</CardTitle>
            <CardDescription>Invite people to join the community</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {referralStats && (
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center p-2 rounded-lg bg-muted/30">
                  <p className="text-lg font-bold">{referralStats.total}</p>
                  <p className="text-xs text-muted-foreground">Sent</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-green-500/5">
                  <p className="text-lg font-bold text-green-500">{referralStats.joined}</p>
                  <p className="text-xs text-muted-foreground">Joined</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-amber-500/5">
                  <p className="text-lg font-bold text-amber-500">{referralStats.pending}</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Input placeholder="Enter email address..." value={referralEmail} onChange={e => setReferralEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSendReferral()} className="input-default" data-testid="referral-email-input" />
              <Button onClick={handleSendReferral} disabled={sendingReferral || !referralEmail.trim()} className="btn-primary" data-testid="send-referral-btn">
                {sendingReferral ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            {referrals.length > 0 && (
              <div className="space-y-2 mt-4">
                <h4 className="text-sm font-medium text-muted-foreground">Your Referrals</h4>
                {referrals.slice(0, 5).map(ref => (
                  <div key={ref.referral_id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 text-sm" data-testid={`referral-${ref.referral_id}`}>
                    <span className="truncate">{ref.referred_email}</span>
                    <Badge variant={ref.status === 'joined' ? 'default' : 'outline'} className={`text-xs ${ref.status === 'joined' ? 'bg-green-500/10 text-green-500' : ''}`}>
                      {ref.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notification Preferences */}
        {notifPrefs && (
          <Card className="card-default" data-testid="notification-prefs">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg"><Bell className="h-5 w-5 text-primary" /> Notification Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'email_notifications', label: 'Email Notifications', desc: 'Receive email for important updates' },
                { key: 'push_notifications', label: 'Push Notifications', desc: 'Browser push notifications' },
                { key: 'message_notifications', label: 'Message Notifications', desc: 'New messages and chat activity' },
                { key: 'event_notifications', label: 'Event Notifications', desc: 'Event updates and reminders' },
                { key: 'marketing_notifications', label: 'Marketing', desc: 'News and promotional content' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{label}</p>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                  <Switch checked={notifPrefs[key] ?? true} onCheckedChange={v => setNotifPrefs(prev => ({ ...prev, [key]: v }))} data-testid={`pref-${key}`} />
                </div>
              ))}
              <Button onClick={handleSavePrefs} disabled={savingPrefs} className="w-full btn-primary mt-4" data-testid="save-prefs-btn">
                {savingPrefs ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Preferences
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
